import React from 'react';
import classNames from 'classnames';
import { useRightPanel } from '../../RightPanelContext';
import { IconOpenLeft, FilterUnconstrain, FilterConstrain } from '../../../../../../components/Icons';
import './ModuleSwitcher.scss';
import { Whisper, Tooltip } from 'rsuite';
import { useReportsStore } from '../../../../../../hooks/useReportsStore';
import { useNTUtilsContext } from '../../../NTUtilsContext';

interface ModuleSwitcherProps {
  onClose?: () => void;
  onExpand?: () => void;
  isExpanded?: boolean;
}

const ModuleSwitcher: React.FC<ModuleSwitcherProps> = ({ onClose, onExpand, isExpanded = false }) => {
  const { modules, activeModuleId, setActiveModuleId, setPanelVisible } = useRightPanel();
  const { actions, setActions } = useNTUtilsContext();
  const { aiReport, setAiReport, reportQueue, setReportQueue } = useReportsStore(state => ({
    aiReport: state.aiReport,
    setAiReport: state.setAiReport,
    reportQueue: state.reportQueue,
    setReportQueue: state.setReportQueue,
  }));
  const primaryModuleIds = ['chart', 'ai-analysis', 'report-queue'];
  const primaryModules = primaryModuleIds
    .map(moduleId => modules.find(module => module.id === moduleId))
    .filter(Boolean);
  // Handle module selection
  const handleModuleSelect = (moduleId: string) => {
    if (moduleId === 'ai-analysis') {
      if (!aiReport) {
        setAiReport(true);
      }
      if (reportQueue) {
        setReportQueue(false);
      }
    } else if (moduleId === 'report-queue') {
      if (!reportQueue) {
        setReportQueue(true);
      }
      if (aiReport) {
        setAiReport(false);
      }
    } else {
      if (reportQueue) {
        setReportQueue(false);
      }
      if (aiReport) {
        setAiReport(false);
      }
      if (!actions.detailsPanel) {
        setActions({ ...actions, detailsPanel: true });
        setPanelVisible(true);
      }
    }
    setActiveModuleId(moduleId);
  };

  return (
    <div className="module-switcher">
      {/* Left side controls */}

      <div className="module-switcher-controls">
        {onClose && (
          <Whisper
            trigger="hover"
            placement="top"
            speaker={<Tooltip>Hide Details Panel</Tooltip>}
            delayOpen={800}
            delayClose={100}
          >
            <button className="control-button close-button" onClick={onClose} aria-label="Close panel">
              <div className="module-icon">
                <IconOpenLeft />
              </div>
            </button>
          </Whisper>
        )}

        {onExpand && modules.find(module => module.id === activeModuleId)?.expandEnabled && (
          <Whisper
            trigger="hover"
            placement="top"
            speaker={<Tooltip>{isExpanded ? 'Collapse Details Panel (e)' : 'Expand Details Panel (e)'}</Tooltip>}
            delayOpen={800}
            delayClose={100}
          >
            <button
              className="control-button expand-button"
              onClick={onExpand}
              aria-label={isExpanded ? 'Collapse panel (e)' : 'Expand panel (e)'}
            >
              <div className="module-icon">{isExpanded ? <FilterConstrain /> : <FilterUnconstrain />}</div>
            </button>
          </Whisper>
        )}
      </div>

      {/* Right side module tabs */}
      <div className="module-tabs">
        {primaryModules.map((module, index) => (
          <button
            key={module.id}
            className={classNames('module-tab', {
              active: module.id === activeModuleId,
            })}
            onClick={() => handleModuleSelect(module.id)}
            aria-label={`Switch to ${module.name} module`}
            aria-pressed={module.id === activeModuleId}
          >
            <Whisper
              trigger="hover"
              placement={index === primaryModules.length - 1 ? 'left' : 'top'}
              speaker={
                <Tooltip>
                  {module.keyboardShortcut ? `${module.name} (${module.keyboardShortcut})` : module.name}
                </Tooltip>
              }
              delayOpen={500}
              delayClose={100}
            >
              <div className="module-icon" style={{ width: module.iconSize || 24, height: module.iconSize || 24 }}>
                {module.icon}
              </div>
            </Whisper>
          </button>
        ))}

        {primaryModules.length === 0 && <div className="no-modules">No modules available</div>}
      </div>
    </div>
  );
};

export default ModuleSwitcher;
